
import React, { useState, useEffect } from 'react';
import { signInWithPopup, signInWithRedirect, GoogleAuthProvider, getRedirectResult } from "firebase/auth";
import { auth, isFirebaseConfigured } from "../firebaseConfig";
import { AlertTriangle, XCircle, Terminal, Lock, ArrowRight, X } from 'lucide-react';

interface LoginScreenProps {
  onGuestLogin?: () => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onGuestLogin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{message: string, code?: string, domains?: string[]} | null>(null);
  
  // Dev Mode Security State
  const [showDevAuth, setShowDevAuth] = useState(false);
  const [devPass, setDevPass] = useState('');

  const handleFirebaseError = (err: any) => {
    console.error("Firebase Auth Error:", err);
    let errorMsg = 'Error desconocido al iniciar sesión.';
    const errorCode = err.code;
    
    if (errorCode === 'auth/popup-closed-by-user') {
       errorMsg = 'Has cerrado la ventana de inicio de sesión.';
    } else if (errorCode === 'auth/unauthorized-domain') {
       errorMsg = `Dominio no autorizado.`;
    } else if (errorCode === 'auth/popup-blocked') {
       errorMsg = 'El navegador bloqueó la ventana emergente.';
    }

    setError({ message: errorMsg, code: errorCode });
    setLoading(false);
  };

  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result) console.log("Login exitoso por redirección");
      })
      .catch((err) => {
        if (err.code !== 'auth/unauthorized-domain') {
            handleFirebaseError(err);
        }
      });
  }, []);

  const handleLogin = async (method: 'popup' | 'redirect') => {
    setLoading(true);
    setError(null);
    const provider = new GoogleAuthProvider();
    
    try {
      if (method === 'popup') {
        await signInWithPopup(auth, provider);
      } else {
        await signInWithRedirect(auth, provider);
      }
    } catch (err: any) {
      handleFirebaseError(err);
    }
  };

  const handleDevLoginAttempt = () => {
    if (devPass === 'QPZM12!@') {
       if (onGuestLogin) onGuestLogin();
    } else {
       setError({ message: 'Clave de desarrollador incorrecta' });
       setDevPass('');
    }
  };

  if (!isFirebaseConfigured) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-xl border border-gray-200 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center text-amber-50 mx-auto mb-4">
            <AlertTriangle size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Configuración Necesaria</h1>
          <p className="text-gray-500">Conecta tu base de datos Firebase para continuar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center p-4 relative font-sans overflow-hidden">
      
      {/* Background Abstract Shapes */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
         <div className="absolute top-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-blue-100/40 blur-3xl opacity-60"></div>
         <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-teal-100/40 blur-3xl opacity-60"></div>
      </div>

      <div className="w-full max-w-[400px] z-10 animate-in fade-in zoom-in duration-500 flex flex-col">
        
        {/* Main Card */}
        <div className="bg-white rounded-[32px] shadow-2xl shadow-blue-900/5 border border-white p-8 md:p-10 relative overflow-hidden">
            
            {/* Logo Section (DYNAMIC MODULAR ASSEMBLY) */}
            <div className="flex flex-col items-center mb-10">
               <div className="relative w-28 h-28 mb-4">
                  
                  {/* Custom Animations for Rolling/Travel Effect */}
                  <style>
                    {`
                      @keyframes rollInTL {
                        0% { opacity: 0; transform: translate(-120%, -120%) rotate(-180deg); }
                        100% { opacity: 1; transform: translate(0, 0) rotate(0deg); }
                      }
                      @keyframes rollInTR {
                        0% { opacity: 0; transform: translate(120%, -120%) rotate(180deg); }
                        100% { opacity: 1; transform: translate(0, 0) rotate(0deg); }
                      }
                      @keyframes rollInBL {
                        0% { opacity: 0; transform: translate(-120%, 120%) rotate(-180deg); }
                        100% { opacity: 1; transform: translate(0, 0) rotate(0deg); }
                      }
                      @keyframes rollInBR {
                        0% { opacity: 0; transform: translate(120%, 120%) rotate(180deg); }
                        100% { opacity: 1; transform: translate(0, 0) rotate(0deg); }
                      }
                      @keyframes assembleText {
                        0% { opacity: 0; transform: translateY(20px) scale(0.8); }
                        100% { opacity: 1; transform: translateY(0) scale(1); }
                      }
                      
                      .module-tl { animation: rollInTL 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
                      .module-tr { animation: rollInTR 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s forwards; opacity: 0; }
                      .module-bl { animation: rollInBL 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s forwards; opacity: 0; }
                      .module-br { animation: rollInBR 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.45s forwards; opacity: 0; }
                      
                      /* Staggered text animation */
                      .letter-anim { opacity: 0; animation: assembleText 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
                    `}
                  </style>

                  {/* Modular Grid Container */}
                  <div className="grid grid-cols-2 grid-rows-2 gap-2 w-full h-full p-2 relative z-10">
                      {/* Top Left Module */}
                      <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl shadow-lg module-tl">
                      </div>
                      
                      {/* Top Right Module */}
                      <div className="bg-gradient-to-bl from-blue-500 to-indigo-500 rounded-2xl shadow-lg module-tr">
                      </div>
                      
                      {/* Bottom Left Module */}
                      <div className="bg-gradient-to-tr from-indigo-600 to-blue-600 rounded-2xl shadow-lg module-bl">
                      </div>
                      
                      {/* Bottom Right Module */}
                      <div className="bg-gradient-to-tl from-indigo-500 to-purple-500 rounded-2xl shadow-lg module-br">
                      </div>
                  </div>

                  {/* Outer Glow */}
                  <div className="absolute inset-0 bg-blue-400/20 blur-2xl rounded-full -z-10 animate-pulse"></div>
               </div>

               {/* Title - NEW AESTHETICS: ROUNDED, ASSEMBLED, LIGHTER */}
               <div className="flex items-center justify-center space-x-1 mb-2 font-['Outfit'] font-black text-4xl tracking-tight text-slate-600">
                  {'MODULAR'.split('').map((char, i) => (
                    <span 
                      key={i} 
                      className="letter-anim" 
                      style={{ animationDelay: `${0.6 + (i * 0.08)}s` }}
                    >
                      {char}
                    </span>
                  ))}
               </div>
               
               {/* SUBTITLE: Restored visibility by removing opacity-0 and ensuring standard animation */}
               <div className="flex items-center space-x-3 animate-in fade-in slide-in-from-bottom-2 duration-1000">
                  <span className="h-px w-8 bg-gradient-to-r from-transparent to-slate-300"></span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.25em] font-['Outfit']">RADIOLOGY MODULAR DESKTOP</span>
                  <span className="h-px w-8 bg-gradient-to-l from-transparent to-slate-300"></span>
               </div>
            </div>

            {/* Login Actions */}
            <div className="space-y-4">
               {/* Google Button (Primary) */}
               <button
                  onClick={() => handleLogin('popup')}
                  disabled={loading}
                  className="w-full flex items-center justify-center space-x-3 bg-white hover:bg-gray-50 text-slate-600 font-bold py-3.5 px-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all active:scale-[0.98] group"
               >
                  {loading ? (
                     <div className="w-5 h-5 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin"></div>
                  ) : (
                     <>
                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5 group-hover:scale-110 transition-transform" alt="G" />
                        <span className="font-['Outfit'] font-bold">Continuar con Google</span>
                     </>
                  )}
               </button>

               {/* Error Message */}
               {error && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex items-start space-x-2 mt-4 animate-in fade-in slide-in-from-top-1">
                     <XCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
                     <p className="text-xs text-red-600 font-medium">{error.message}</p>
                  </div>
               )}
            </div>

            {/* Bottom Quadrant / Secondary Actions */}
            <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between items-end">
                <div className="flex flex-col">
                   <div className="text-[10px] text-gray-300 font-medium select-none font-['Outfit']">v4.0.3</div>
                   <div className="text-[9px] text-blue-300 font-bold uppercase tracking-wider mt-0.5 opacity-0 animate-in fade-in delay-1000 duration-1000 fill-mode-forwards">PROD</div>
                </div>
                
                {/* Developer Mode Protected Button */}
                {onGuestLogin && (
                   <div className="flex items-center">
                     {showDevAuth ? (
                        <div className="flex items-center space-x-2 animate-in fade-in slide-in-from-right-2 bg-slate-50 p-1 rounded-lg border border-slate-200">
                           <div className="flex items-center bg-white border border-gray-200 rounded px-2 py-1">
                              <Lock size={10} className="text-gray-400 mr-1" />
                              <input 
                                 type="password" 
                                 value={devPass}
                                 onChange={(e) => setDevPass(e.target.value)}
                                 onKeyDown={(e) => e.key === 'Enter' && handleDevLoginAttempt()}
                                 placeholder="Clave..."
                                 className="w-20 text-[10px] outline-none bg-transparent text-gray-900 font-['Outfit']"
                                 autoFocus
                              />
                           </div>
                           <button 
                              onClick={handleDevLoginAttempt}
                              className="bg-slate-800 text-white p-1 rounded hover:bg-slate-700 transition-colors"
                           >
                              <ArrowRight size={12} />
                           </button>
                           <button 
                              onClick={() => { setShowDevAuth(false); setError(null); setDevPass(''); }}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                           >
                              <X size={14} />
                           </button>
                        </div>
                     ) : (
                        <button
                           onClick={() => setShowDevAuth(true)}
                           className="group flex flex-col items-center justify-center p-2 rounded-lg hover:bg-slate-50 transition-colors"
                           title="Acceso Desarrollador"
                        >
                           <div className="bg-slate-100 text-slate-400 group-hover:bg-slate-800 group-hover:text-white p-1.5 rounded-md transition-all">
                              <Terminal size={14} />
                           </div>
                        </button>
                     )}
                   </div>
                )}
            </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-500">
           <p className="text-[10px] font-bold text-slate-300 tracking-wider flex items-center justify-center gap-1 font-['Outfit']">
              <span>POWERED BY</span>
              <span className="text-slate-400">IMAGEN X.LLC</span>
           </p>
        </div>

      </div>
    </div>
  );
};

export default LoginScreen;
